import { useCallback, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { RegisterCredentials } from '../types/auth'
import Navigation from '../components/Navigation'
import Logo from '../components/Logo'
import { COUNTRIES } from '../constants/countries'
import TurnstileWidget from '../components/TurnstileWidget'

export default function RegisterPage() {
  const { register, loading } = useAuth()
  const navigate = useNavigate()
  const [formData, setFormData] = useState<RegisterCredentials>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    displayName: '',
    country: '',
    turnstileToken: ''
  })
  const [errors, setErrors] = useState<Partial<RegisterCredentials & { confirmPassword: string }>>({})
  const [submitError, setSubmitError] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0)

  const handleTurnstileTokenChange = useCallback((token: string) => {
    setFormData(previous => ({ ...previous, turnstileToken: token }))
    if (token) {
      setErrors(previous => ({ ...previous, turnstileToken: undefined }))
    }
  }, [])

  const validateForm = (): boolean => {
    const newErrors: Partial<RegisterCredentials & { confirmPassword: string }> = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    if (formData.password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (!formData.firstName) {
      newErrors.firstName = 'First name is required'
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required'
    }

    if (!formData.displayName) {
      newErrors.displayName = 'Display name is required'
    }

    if (!formData.country) {
      newErrors.country = 'Country is required'
    }

    if (!formData.turnstileToken) {
      newErrors.turnstileToken = 'Please complete the signup verification'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError('')

    if (!validateForm()) return

    try {
      // Create registration data with all required fields
      const registrationData: RegisterCredentials = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: formData.displayName,
        country: formData.country,
        turnstileToken: formData.turnstileToken,
      }

      await register(registrationData)
      
      // After successful registration, redirect based on email verification status
      // The useAuth hook will set the user state, then App.tsx routing will handle
      // redirecting to email verification if needed
      navigate('/verify-email-required')
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Registration failed')
      setTurnstileResetSignal(value => value + 1)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name as keyof RegisterCredentials]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
    // Clear error when user starts typing
    setErrors(prev => ({ ...prev, confirmPassword: undefined }))
  }

  // Common countries list (you can expand this)
  const countries = COUNTRIES

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <Logo className="w-20 h-20 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Join Fired Up Games</h1>
            <p className="text-gray-400">Create your account to start competing</p>
          </div>

          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
            <form onSubmit={handleSubmit} className="space-y-4">
              {submitError && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-red-300 text-sm">
                  {submitError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                    First Name *
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    className={`input-field ${errors.firstName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                    Last Name *
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    className={`input-field ${errors.lastName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">
                  Display Name *
                </label>
                <input
                  id="displayName"
                  name="displayName"
                  type="text"
                  required
                  className={`input-field ${errors.displayName ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="How you want to appear in leaderboards"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.displayName && (
                  <p className="mt-1 text-sm text-red-400">{errors.displayName}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`input-field ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-300 mb-2">
                  Country *
                </label>
                <select
                  id="country"
                  name="country"
                  required
                  className={`input-field ${errors.country ? 'border-red-500 focus:ring-red-500' : ''}`}
                  value={formData.country}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">Select your country</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {errors.country && (
                  <p className="mt-1 text-sm text-red-400">{errors.country}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Password *
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={`input-field ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm Password *
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={`input-field ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  disabled={loading}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                )}
              </div>

              <div>
                <TurnstileWidget
                  onTokenChange={handleTurnstileTokenChange}
                  resetSignal={turnstileResetSignal}
                />
                {errors.turnstileToken && (
                  <p className="mt-1 text-sm text-red-400">{errors.turnstileToken}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !formData.turnstileToken}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-firedup-orange hover:text-firedup-red font-medium transition-colors text-sm"
                >
                  Already have an account? Sign in
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
